"use client";

import { KeyboardEvent, MouseEvent, useState, useContext } from "react";
import { useRouter } from "next/navigation";
import CartIcon from '@components/common/CartIcon';
import Image from '@components/common/ImageFallback';
import { DataContextCart } from '@root/src/contextcart';
import getProductHref from '@root/src/lib/getProductHref';

interface Product {
  id: number;
  name: string;
  price: string;
  badge?: string;
  img: string;
  slug?: string;
  totalQuantity?: number;
}

function CheckIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export default function ProductCard({ product }: { product: Product }) {
  const [added, setAdded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const router = useRouter();
  const contextCart: any = useContext(DataContextCart);
  const { dataCart = [], updateCart } = contextCart || {};
  const totalQuantity = Number(product.totalQuantity || 0);
  const isOutOfStock = totalQuantity <= 0;
  const cartQuantity = Number(
    (dataCart || []).find((item: any) => item.productId === product.id)?.quantity || 0
  );
  const isAtStockLimit = !isOutOfStock && cartQuantity >= totalQuantity;

  const productHref = getProductHref(product);

  const handleCardClick = () => {
    if (!productHref) return;
    router.push(productHref);
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!productHref || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    router.push(productHref);
  };

  const handleAdd = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isOutOfStock || isAtStockLimit) return;
    let currentCart = [...(dataCart || [])];
    const priceNumber = Number(String(product.price).replace(/\D/g, '')) || 0;
    const newProduct = {
      productName: product.name,
      discount: 0,
      productPrice: priceNumber,
      productImage: product.img || '',
      quantity: 1,
      productId: product.id,
    };
    const existingIndex = currentCart.findIndex(
      (item: any) => item.productId === newProduct.productId
    );
    if (existingIndex >= 0) {
      currentCart[existingIndex].quantity += 1;
    } else {
      currentCart.push(newProduct);
    }
    updateCart?.(currentCart);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div
      role={productHref ? 'link' : undefined}
      tabIndex={productHref ? 0 : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      style={{
        width: '100%',
        position: "relative",
        background: "#ffffff",
        borderRadius: 12,
        overflow: "hidden",
        cursor: "pointer",
        border: "1px solid #ede5d4",
        transition: "transform 0.22s ease, box-shadow 0.22s ease",
        transform: hovered ? "translateY(-5px)" : "translateY(0)",
        boxShadow: hovered
          ? "0 16px 40px rgba(44,74,46,0.12)"
          : "0 2px 8px rgba(44,74,46,0.04)",
      }}
    >
      {/* Badge */}
      {product.badge && (
        <span
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 10,
            background: "#4a5e3a",
            color: "#ffffff",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.12em",
            padding: "4px 10px",
            borderRadius: 4,
          }}
        >
          {product.badge}
        </span>
      )}

      {/* Image */}
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "1 / 1",
          overflow: "hidden",
        }}
      >
        <Image
          src={product.img}
          alt={product.name}
          fill
          quality={60}
          sizes="(max-width: 640px) calc(50vw - 20px), (max-width: 1024px) calc(33vw - 24px), 320px"
          style={{
            objectFit: "cover",
            transition: "transform 0.4s ease",
            transform: hovered ? "scale(1.05)" : "scale(1)",
          }}
        />
        {/* overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to top, rgba(245,237,224,0.15) 0%, transparent 60%)",
            pointerEvents: "none",
          }}
        />
      </div>

      {/* Info */}
      <div style={{ padding: "14px 16px 16px" }}>
        <h3
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 15,
            fontWeight: 500,
            color: "#2c4a2e",
            margin: "0 0 10px",
            lineHeight: 1.3,
          }}
        >
          {product.name}
        </h3>

        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <span
              style={{
                display: "block",
                fontSize: 15,
                fontWeight: 700,
                color: "#2c4a2e",
              }}
            >
              {product.price}
            </span>
            <span
              style={{
                display: "block",
                marginTop: 6,
                fontSize: 12,
                fontWeight: 600,
                color: isOutOfStock ? "#a14b4b" : "#7d735f",
              }}
            >
              {isOutOfStock
                ? "Liên hệ 0931 338 566"
                : isAtStockLimit
                  ? `Đã thêm tối đa: ${totalQuantity}`
                  : `Còn lại: ${totalQuantity}`}
            </span>
          </div>

          <button
            onClick={handleAdd}
            aria-label={`Thêm ${product.name} vào giỏ`}
            disabled={isOutOfStock || isAtStockLimit}
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              border: `1.5px solid ${isOutOfStock ? "#c8bfae" : "#4a5e3a"}`,
              background: added ? "#4a5e3a" : "transparent",
              color: isOutOfStock
                ? "#c8bfae"
                : added
                  ? "#ffffff"
                  : "#4a5e3a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: isOutOfStock ? "not-allowed" : "pointer",
              flexShrink: 0,
              marginRight: 2,
              opacity: isOutOfStock ? 0.6 : 1,
              transition: "background 0.18s, color 0.18s",
            }}
          >
            {added ? <CheckIcon /> : <CartIcon />}
          </button>
        </div>
      </div>
    </div>
  );
}
